import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
    MaterialWithDimensions,
    Literal,
    getDimensionUnit,
    getTradeUnit,
    Product,
    ThicknessOption,
    ThicknessType,
} from '@steelbuy/ts-shared';
import {
    ButtonSize,
    ButtonTertiary,
    Card,
    IconIdentifier,
    Notification,
    NotificationLevel,
} from '@steelbuy/ui-primitive';
import { useWindowSize } from '@steelbuy/util';
import { RFQInputs, isUniqueEntry, removeNullFields, mapInputs, updateNonApplicableFields } from '../../createRFQUtil';
import { useRfqFormContext } from '../../RfqFormContext';
import { Row } from '../rfq-table/RFQTable';

import './AddRFQItem.scss';

interface AddRFQItemProps {
    append: (data: MaterialWithDimensions) => void;
    headings: RFQInputs;
    material: MaterialWithDimensions;
    fields: MaterialWithDimensions[];
    hasAddedRows: boolean;
    draftId: string | null;
    stepVisited: boolean;
    saveDraftCounter: number;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RFQNumber = any;

export const AddRFQItem = ({
    append,
    material,
    headings,
    fields,
    hasAddedRows,
    draftId,
    stepVisited,
    saveDraftCounter,
}: AddRFQItemProps) => {
    const { t } = useTranslation('translation');
    const [error, setError] = useState('');
    const triggerOnChange = useRef(false);
    const { width } = useWindowSize();
    const [isAddButtonWrapped, setIsAddButtonWrapped] = useState(false);
    const methods = useForm<MaterialWithDimensions>({
        defaultValues: {
            grade: undefined,
            specification: undefined,
            finish: undefined,
            width: '' as RFQNumber,
            weight: '' as RFQNumber,
            thickness: '' as RFQNumber,
            thicknessMin: '' as RFQNumber,
            thicknessMax: '' as RFQNumber,
            thicknessOption: ThicknessOption.SINGLE,
            thicknessType: material.product === Product.SHEET ? ThicknessType.NOM : undefined,
            length: '' as RFQNumber,
            tolerance: undefined,
            surface: undefined,
            coatingThicknessValue: '' as RFQNumber,
            gradeFamily: undefined,
            coatingWeight: undefined,
            surfaceTreatment: undefined,
            compliance: undefined,
            surfaceFinish: undefined,
            deliveryAddress: undefined,
        },
        mode: 'all',
    });

    const {
        control,
        handleSubmit,
        watch,
        reset,
        formState: { isValid, errors, isDirty },
        clearErrors,
        setValue,
        trigger,
    } = methods;

    const { cancelEditRow, warehouseAddresses } = useRfqFormContext();
    const showAddressSelector = warehouseAddresses.length > 0;

    useEffect(() => {
        updateNonApplicableFields(headings, { ...material, ...watch() }, clearErrors, watch, setValue, errors);
        if (triggerOnChange.current && draftId) {
            triggerOnChange.current = false;
            trigger();
        }
    }, [watch(), draftId]);

    useEffect(() => {
        if ((stepVisited || draftId) && !hasAddedRows) {
            trigger();
        }
    }, [draftId, hasAddedRows, stepVisited, isDirty, trigger]);

    useEffect(() => {
        reset();
        setError('');
        if (!hasAddedRows) {
            triggerOnChange.current = true;
        }
    }, [saveDraftCounter, reset]);

    const thicknessOptionValue = watch('thicknessOption');

    useEffect(() => {
        if (thicknessOptionValue === ThicknessOption.RANGE) {
            setValue('thickness', '' as RFQNumber);
            clearErrors('thickness');
        } else {
            setValue('thicknessMin', '' as RFQNumber);
            setValue('thicknessMax', '' as RFQNumber);
            clearErrors('thicknessMin');
            clearErrors('thicknessMax');
            if (material.product === Product.SHEET) {
                setValue('thicknessType', ThicknessType.NOM);
            }
        }
    }, [thicknessOptionValue]);

    const tbodyRef = useRef<HTMLTableSectionElement>(null);
    const addButtonRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (tbodyRef.current && addButtonRef.current) {
            setIsAddButtonWrapped(
                addButtonRef.current?.getBoundingClientRect().bottom > tbodyRef.current.getBoundingClientRect().bottom
            );
        }
    }, [watch(), width]);

    return (
        <section className="add-rfq-item">
            <Card isClickable={false}>
                <div className="add-rfq-item__container">
                    <p className="add-rfq-item__title">{t('application.addRfq.addTitle')}</p>
                    <p className="add-rfq-item__description">{t('application.addRfq.addDescription')}</p>
                </div>
                <div className="add-rfq-item__content">
                    <table>
                        <thead>
                            <tr className="add-rfq-item__label">
                                {Object.entries(headings).map(
                                    ([key, { disabledFn, measurementLiteral, measurementType }]) => {
                                        let measurementText = '';
                                        if (measurementType !== undefined) {
                                            const literal =
                                                measurementType === 'dimension'
                                                    ? `domainModel:material.dimensions.units.${getDimensionUnit()}.label`
                                                    : (`domainModel:material.tradeUnit.value.${getTradeUnit({
                                                          materialType: material.materialType,
                                                          product: material.product,
                                                      })}.label` as Literal);
                                            measurementText = ` (${t(literal)})`;
                                        } else if (measurementLiteral) {
                                            const literalText = t(`domainModel:${measurementLiteral}` as Literal);
                                            measurementText = ` (${literalText})`;
                                        }
                                        return (
                                            <td
                                                key={key}
                                                className={classNames({
                                                    'add-rfq-item__label--disabled': disabledFn?.({
                                                        ...material,
                                                        ...watch(),
                                                    }),
                                                })}
                                            >
                                                <div className="add-rfq-item__label__container">
                                                    <span>{t(`application.rfqHeadingLabels.${key}` as Literal)}</span>
                                                    {measurementText ? <span>{measurementText}</span> : null}
                                                    {(!disabledFn?.({
                                                        ...material,
                                                        ...watch(),
                                                    }) ||
                                                        (key === 'thicknessType' &&
                                                            watch('thicknessOption') === ThicknessOption.SINGLE)) && (
                                                        <span className="required-field"> *</span>
                                                    )}
                                                </div>
                                            </td>
                                        );
                                    }
                                )}
                                {showAddressSelector && (
                                    <td>
                                        <div className="add-rfq-item__label__container">
                                            <span>{t('application.rfqHeadingLabels.deliveryAddress' as Literal)}</span>
                                            <span className="required-field"> *</span>
                                        </div>
                                    </td>
                                )}
                            </tr>
                        </thead>
                        <tbody ref={tbodyRef}>
                            <FormProvider {...methods}>
                                <Row
                                    headings={headings}
                                    material={{
                                        ...material,
                                        ...watch(),
                                    }}
                                    control={control}
                                    isEdit
                                    errors={errors}
                                    warehouseAddresses={warehouseAddresses}
                                    deliveryAddressRequired={showAddressSelector}
                                />
                            </FormProvider>
                        </tbody>
                    </table>
                    <div
                        className={classNames('add-rfq-item__content__add-button', {
                            'add-rfq-item__content__add-button--wrapped': isAddButtonWrapped,
                        })}
                        ref={addButtonRef}
                    >
                        <ButtonTertiary
                            label={t('application.createRFQ.addToRFQ')}
                            size={ButtonSize.S}
                            icon={IconIdentifier.PLUS}
                            type="submit"
                            onClick={handleSubmit(
                                (data) => {
                                    if (isUniqueEntry(data, fields)) {
                                        cancelEditRow();
                                        setError('');
                                        let mappedValues = removeNullFields(data);
                                        mappedValues = mapInputs(mappedValues);
                                        append(mappedValues);
                                        reset();
                                    } else {
                                        setError(t('application.addRfq.itemExists'));
                                    }
                                },
                                () => setError(t('application.addRfq.genericError'))
                            )}
                            disabled={!isValid}
                        />
                    </div>
                </div>
                {!!error && <Notification level={NotificationLevel.ERROR} message={error} stayOpen disableScroll />}
            </Card>
        </section>
    );
};
